import React, { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { JobCard } from "@/components/JobCard";
import { EmptyState, Loading } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function JobDiscovery() {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [cats, setCats] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [f, setF] = useState({ keyword: "", city: "", category_id: "all", min_payment: "", max_payment: "", sort: "recent" });

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const load = useCallback(async (override) => {
    const cur = override || f;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cur.keyword) params.set("keyword", cur.keyword);
      if (cur.city) params.set("city", cur.city);
      if (cur.category_id !== "all") params.set("category_id", cur.category_id);
      if (cur.min_payment) params.set("min_payment", cur.min_payment);
      if (cur.max_payment) params.set("max_payment", cur.max_payment);
      params.set("sort", cur.sort);
      const { data } = await api.get(`/jobs?${params.toString()}`);
      setJobs(data);
    } finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [f.sort, f.category_id]);

  const reset = () => {
    const cleared = { keyword: "", city: "", category_id: "all", min_payment: "", max_payment: "", sort: "recent" };
    setF(cleared);
    load(cleared);
  };

  return (
    <div className="space-y-5" data-testid="job-discovery">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold">Cari Pekerjaan</h1>
        <p className="text-slate-500 mt-1">Temukan pekerjaan fleksibel di sekitarmu.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-keyword" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} placeholder="Cari judul, deskripsi, kota..." className="pl-10 h-11" />
        </div>
        <Button data-testid="search-btn" type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-lg h-11">Cari</Button>
        <Button data-testid="filter-toggle" type="button" variant="outline" onClick={() => setShowFilter(!showFilter)} className="h-11 rounded-lg border-slate-200">
          <SlidersHorizontal size={18} />
        </Button>
      </form>

      {showFilter && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-up">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Kota</label>
            <Input data-testid="filter-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Medan" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Kategori</label>
            <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
              <SelectTrigger data-testid="filter-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua kategori</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Bayaran min</label>
            <Input data-testid="filter-min" type="number" value={f.min_payment} onChange={(e) => setF({ ...f, min_payment: e.target.value })} placeholder="0" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Bayaran max</label>
            <Input data-testid="filter-max" type="number" value={f.max_payment} onChange={(e) => setF({ ...f, max_payment: e.target.value })} placeholder="500000" className="mt-1" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
            <Button data-testid="filter-apply" onClick={load} className="bg-blue-600 hover:bg-blue-700 rounded-lg">Terapkan Filter</Button>
            <Button data-testid="filter-reset" onClick={reset} variant="ghost" className="text-slate-500"><X size={16} className="mr-1" /> Reset</Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{jobs.length} pekerjaan ditemukan</p>
        <Select value={f.sort} onValueChange={(v) => setF({ ...f, sort: v })}>
          <SelectTrigger data-testid="sort-select" className="w-44 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Terbaru</SelectItem>
            <SelectItem value="payment">Bayaran tertinggi</SelectItem>
            <SelectItem value="nearest">Terdekat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : jobs.length === 0 ? (
        <EmptyState icon={Search} title="Tidak ada pekerjaan ditemukan." description="Coba ubah kata kunci atau reset filter." actionLabel="Reset Filter" onAction={reset} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
        </div>
      )}
    </div>
  );
}
